import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import styles from './SideNavigation.module.scss';
import {
	SideNavOpen,
	ActiveShipper,
	ActiveCarrier,
	IsAdmin,
	UserRoles,
	ShipperSettings,
	authenticatedUser
} from '@state';
import { useRecoilState, useRecoilValue } from 'recoil';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import { LogoIcon } from '@img/icons';
import { Text, Caption, TypeAhead, toast } from '@components';
import { useLocation } from 'react-router-dom';
import { Link } from '@components';
import shipperNav from './navigationShipper';
import carrierNav from './navigationCarrier';
import inboxNav from './navigationInbox';
import transportNav from './navigationTransport';
import financeNav from './navigationFinance';
import configNav from './navigationConfiguration';
import {
	navigator,
	TypeAheadOption,
	ShipperSearch,
	CarrierSearch,
	TEXT_WEIGHT
} from '@types';
import { useGetCarrier, useGetShipper } from '@api';
import { debounce } from 'lodash';
import { useImpersonation, useFeatureFlag } from '@hooks';
import { useGetYourLoadCount } from '@api';

export const SideNavigation = () => {
	const [sideNavOpen, setSideNavOpen] = useRecoilState(SideNavOpen);
	const [activeShipper] = useRecoilState(ActiveShipper);
	const [activeCarrier] = useRecoilState(ActiveCarrier);
	const userRoles = useRecoilValue(UserRoles);
	const isAdmin = useRecoilValue(IsAdmin);
	const [search, setSearch] = useState('');
	const [searchOpts, setSearchOpts] = useState<TypeAheadOption[]>([]);
	const location = useLocation();
	const shipperSettings = useRecoilValue(ShipperSettings);
	const { impersonate, refrain } = useImpersonation();
	const [yourLoadCount, setYourLoadCount] = useState<number | null>(0);
	const authUser = useRecoilValue(authenticatedUser);
	const {
		yourLoad,
		callGetYourLoadCount
	} = useGetYourLoadCount();

	const DEMURRAGE_CLAIMS_ACTIVE = useFeatureFlag('demurrage_claims');
	const CUSTOMER_FUEL_LEVY_ACTIVE = useFeatureFlag('customer_fuel_levy');

	const {
		loadingShippers,
		shippers,
		getShipperError,
		callGetShippers
	} = useGetShipper();
	const {
		loadingCarriers,
		carriers,
		getCarrierError,
		callGetCarriers
	} = useGetCarrier();

	useEffect(() => {
		setYourLoadCount(yourLoad?.count ?? 0)
	}, [yourLoad])

	const callSearchAPI = useCallback(debounce((type: 'carrier' | 'shipper', val: string) => {
		type === 'shipper' ? callGetShippers(type, val) : callGetCarriers(type, val);

	}, 500), []);

	const currentPage = (label:string) => {
		const parts = location.pathname.split('/');
		return parts[1].toLocaleLowerCase() === label.toLowerCase();
	}

	useEffect(() => {
		if (currentPage('carrier') ) {
			callGetYourLoadCount();
		}
	}, [location])

	const onSelectChange = (v:ChangeEvent<HTMLInputElement> | string | TypeAheadOption, fromSearch:boolean) => {
		if (fromSearch) {
			if (currentPage('shipper')) {
				impersonate('shipper', (v as TypeAheadOption).value as ShipperSearch);
				setSearch(((v as TypeAheadOption).value as ShipperSearch).trade_name ?? '');
			} else {
				impersonate('carrier', (v as TypeAheadOption).value as CarrierSearch);
				setSearch(((v as TypeAheadOption).value as CarrierSearch).trade_name ?? '');
			}
			setSearchOpts([]);
		} else {
			const val = v ? (v as ChangeEvent<HTMLInputElement>).target.value : '';
			setSearch(val);
			if (val !== '') {
				const isShipper = currentPage('shipper');
				callSearchAPI(isShipper ? 'shipper' : 'carrier', val);
			} else if (val === '') {
				if (currentPage('shipper')) {
					refrain('shipper');
				} else {
					refrain('carrier');
				}
			}
		}
	}

	const getNavItems = () => {
		if (currentPage('shipper')) {
			const isShipperActive = activeShipper?.isActive ?? true;
			return shipperNav(
				`${activeShipper?.id ?? ''}`,
				isAdmin,
				userRoles,
				shipperSettings,
				isShipperActive
			) as navigator[];
		}
		if (currentPage('carrier')) return carrierNav(`${activeCarrier?.id ?? ''}`, yourLoadCount ?? 0, isAdmin, authUser ?? undefined, activeCarrier?.isActive ?? true) as navigator[];
		if (currentPage('inbox')) return inboxNav();
		if (currentPage('transport')) return transportNav();
		if (currentPage('finance')) return financeNav(DEMURRAGE_CLAIMS_ACTIVE as boolean);
		if (currentPage('configuration')) return configNav(userRoles, CUSTOMER_FUEL_LEVY_ACTIVE as boolean);
		return [];
	}
	const isActive = (label:string) => {
		const parts = location.pathname.split('/');
		if (parts.length < 3) return false;
		if (currentPage('shipper')) {
			if (label.toLowerCase() === 'dashboard') {
				return parts[2].toLowerCase() === label.toLowerCase();
			} else if (parts.length >= 2) {
				return parts[2].toLowerCase() === label.toLowerCase();
			} else {
				return false;
			}
		} else if (currentPage('carrier')) {
			if (label.toLowerCase() === 'dashboard' && parts.length < 4) {
				return parts[2].toLowerCase() === label.toLowerCase();
			} else if (parts.length >= 3) {
				return parts[2].toLowerCase() === label.toLowerCase();
			} else {
				return false;
			}
		} else if (currentPage('finance')) {
			if (parts.length >= 4 && (label.toLowerCase() === 'shipper' || label.toLowerCase() === 'carrier')) {
				return parts[3] && parts[3].toLowerCase() === label.toLowerCase();
			} else if (parts.length >= 3) {
				return parts[2] && parts[2].toLowerCase() === label.toLowerCase();
			} else {
				return false;
			}
		} else {
			return parts[2].toLowerCase() === label.toLowerCase();
		}
	}

	useEffect(() => {
		if (shippers) {
			const results = shippers?.searchResult?.shipper;
			const searchOpts: TypeAheadOption[] = results?.map((result: ShipperSearch) => {
				return {
					label: <div className={styles.acOption}>
						<Text weight={TEXT_WEIGHT.BOLD}>{`${result.trade_name}${isAdmin && !result.is_active ? ' (Deactivated)' : ''}`}</Text>
						<Text>{result.company_name}</Text>
					</div>,
					value: result
				}
			}) ?? [];
			setSearchOpts(searchOpts);
		}
	}, [shippers]);

	useEffect(() => {
		if (carriers) {
			const results = carriers?.searchResult?.carriers;
			const searchOpts: TypeAheadOption[] = results?.map((result: CarrierSearch) => {
				return {
					label: <div className={styles.acOption}>
						<Text weight={TEXT_WEIGHT.BOLD}>{`${result.trade_name}${isAdmin && !result.is_active ? ' (Deactivated)' : ''}`}</Text>
						<Text>{result.company_name}</Text>
					</div>,
					value: result
				}
			}) ?? [];
			setSearchOpts(searchOpts);
		}
	}, [carriers]);

	useEffect(() => {
		if (getShipperError || getCarrierError) toast.error(`An error occured searching for ${currentPage('shipper') ? 'shipper' : 'carrier'} ${search}`);
	}, [getShipperError, getCarrierError]);

	/* Keep the lookup field in sync with the customer for the domain we're actually on
	   (navigating shipper <-> carrier must update the field, even when both slots are set). */
	useEffect(() => {
		if (currentPage('shipper')) setSearch(activeShipper?.name ?? '');
		else if (currentPage('carrier')) setSearch(activeCarrier?.name ?? '');
	}, [location.pathname, activeShipper, activeCarrier]);

	return (
		<div data-testid={'@Ofload-SideNav'} className={`${styles.container} ${!sideNavOpen ? styles.hidden : ''}`}>
			<button data-testid={'@Ofload-SideNav-HideBtn'} className={styles.hideButton} onClick={() => { setSideNavOpen(false) }} aria-label={'Side Nav Toggle'}>
				<ArrowBackIcon style={{ fontSize: '0.7rem' }} />
			</button>
			<div className={styles.logo}>
				<LogoIcon />
			</div>
			{isAdmin && (currentPage('shipper') || currentPage('carrier')) && <div data-testid={'@Ofload-SearchBar'} className={styles.selectorContainer}>
				<TypeAhead
					name={'SelectShipperCarrier'}
					value={search}
					onChange={(v:ChangeEvent<HTMLInputElement>) => { onSelectChange(v, false) }}
					onSelected={(o:string | TypeAheadOption) => { onSelectChange(o, true) }}
					options={searchOpts}
					placeholder={currentPage('shipper') ? 'Select Shipper' : 'Select Carrier'}
					keepErrorSpace={false}
					onClearInput={(v) => onSelectChange(v, false)}
					showArrowIcon
					isLoadingOptions={loadingShippers || loadingCarriers}
				/>
			</div>}
			{getNavItems().map((nav:navigator, i:number) => {
				const color = isActive(nav.activeURL ?? '') ? 'var(--brand-orange-default)' : 'var(--brand-navy-default)';
				const isDisabled = nav.enabled === false;
				const disabledStyle = isDisabled ? {
					opacity: 0.5, pointerEvents: 'none' as const, cursor: 'not-allowed'
				} : {};
				const linkClassName = `${styles.menuItem} ${isActive(nav.activeURL ?? '') ? styles.activeItem : ''} ${isDisabled ? styles.disabled : ''}`;
				if (isDisabled) {
					return (
						<div
							key={i}
							className={linkClassName}
							style={disabledStyle}
						>
							{nav.icon(color)}
							<Text text={nav.label} color={color} />
						</div>
					);
				}
				return (
					<Link
						key={i}
						to={nav.link}
						domain={nav.domain}
						count={nav.count ?? 0}
						className={linkClassName}
					>
						{nav.icon(color)}
						<Text text={nav.label} color={color} />
					</Link>
				);
			})}
			<div className={styles.tag}>
				<Caption text={'Ev2'} />
			</div>
		</div>
	);
}
